import * as React from 'react';
import styled from 'styled-components';
import {FileObject, FileType} from 'api/library';
import {GalleryValue} from 'api/lesson/types';
import LessonBuilderContext from 'components/LessonBuilder/LessonBuilderContext';
import CardContext from '../../CardContext';
import Upload, {UploadVariant} from 'components/Upload';
import Column from './Column';

export interface GalleryTemplateProps {
  value?: GalleryValue;
}

const Grid = styled.div`
  gap: 10px;
  margin-top: 20px;
  grid-template-columns: repeat(4, 1fr);
  display: grid;
`;

/**
 * This component renders an gallery template.
 * @param {GalleryTemplateProps} props
 * @return {React.ReactElement<GalleryTemplateProps>}
 * @constructor
 */
export default function GalleryTemplate(
    props: GalleryTemplateProps,
): React.ReactElement<GalleryTemplateProps> {
  const cardContext = React.useContext(CardContext);
  const {lesson, setLesson} = React.useContext(LessonBuilderContext);
  const content = lesson?.content || [];
  const value = props.value || [];

  /**
   * Set the value.
   * @param {FileObject | FileObject[]} payload
   */
  function setValue(payload: FileObject | FileObject[]): void {
    if (cardContext.index !== undefined) {
      if (Array.isArray(payload)) {
        payload.map((file) => (
          value.push(file.id)
        ));
      } else {
        value.push(payload.id);
      }

      content[cardContext.index].value = value;

      (lesson && setLesson) && (
        setLesson({
          ...lesson,
          content,
        })
      );
    }
  }

  /**
   * Render upload.
   * @return {JSX.Element}
   */
  function renderUpload(): JSX.Element {
    /**
     * This upload event handler.
     * @param {FileObject} file
     */
    function handleUpload(file: FileObject): void {
      if (cardContext.index !== undefined) {
        setValue(file);
      }
    }

    /**
     * This select event handler.
     * @param {FileObject[]} attached
     */
    function handleSelect(attached: FileObject[]): void {
      if (cardContext.index !== undefined) {
        setValue(attached);
      }
    }

    return (
      <Upload
        type={FileType.Image}
        title="Галерея"
        variant={UploadVariant.Accent}
        onUpload={handleUpload}
        picker={{
          multiple: true,
          onSelect: handleSelect,
        }}
      />
    );
  }

  /**
   * Render grid.
   * @return {JSX.Element}
   */
  function renderGrid(): JSX.Element | undefined {
    return value.length > 0 ? (
      <Grid>
        {value.map((id, index) => (
          <Column
            key={index}
            value={props.value}
            id={id}
            index={index}
          />
        ))}
      </Grid>
    ) : undefined;
  }

  return (
    <React.Fragment>
      {renderUpload()}
      {renderGrid()}
    </React.Fragment>
  );
}
